import {
  Button,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
} from '@mui/material';
import { get, isEmpty, range, toString } from 'lodash';
import { useState } from 'react';
import { type Path } from 'react-hook-form';
import { SidebarAccordion } from 'src/components/accordion';
import { ResourceSelectorSidebar } from 'src/components/widget';
import { ResourceType } from 'src/components/widget/resourceSelectorWidget/resourceSelectorSidebarWidget/ResourceSelectorSidebar.interface';
import {
  QuillTheme,
  RichTextEditor,
  RichTextToolbarOptions,
} from 'src/components/widget/richTextWidget';
import { extractStylesFromHtml } from 'src/components/widget/richTextWidget/RichTextWidget.helpers';
import { FONTS_OPTIONS } from 'src/constants';
import { COD_COMPONENT_CONFIG } from 'src/constants/gcfComponentConfig/gcfCodConfig';
import {
  useGetProjectConfig,
  useModal,
  useRteDefaultStyleValues,
} from 'src/hooks';
import { lang } from 'src/utils';

import { CustomerTypes } from '../../codThankyou/CODThankYouForm.interface';
import { type CodFooterConfig, MessageConfigTypes } from '../CODForm.interface';
import { CustomImageSelectionButton } from '../headerSection/modules/CustomImageSelectionButton';
import { PaymentTypeToggleButton } from '../headerSection/modules/PaymentTypeToggleButton';
import { MessagingTitle } from '../messagingTitle';
import { MessagingAccordionProps } from './MessagingAccordion.interface';

export const MessagingAccordion = (props: MessagingAccordionProps) => {
  const {
    handlePaymentEventType,
    handleSelectedCustomer,
    paymentEventType,
    selectedCustomer,
  } = props;

  const openResourceDrawer = useModal();

  const { data: projectConfig } = useGetProjectConfig();

  const projectFonts =
    projectConfig?.fonts?.map((item: any) => item.name) ?? [];
  const mappedFonts = projectFonts.length > 0 ? projectFonts : FONTS_OPTIONS;

  const rteDefaultStyleValues = useRteDefaultStyleValues(
    props.theme,
    projectConfig,
  );
  // Seed FONT only into the RTE — never the base color. Baking the base color
  // inline overrides the section color (#D6D6D6) and hides the text on the dark
  // COD preview (CC-22307). Color still reaches the preview via the GCF cascade.
  const fontOnlyDefaultStyleValues = {
    fontFamily: rteDefaultStyleValues.fontFamily,
  };

  const [lastSelectedAsset, setLastSelectedAsset] = useState('');

  const selectedKnownCustomerImage = get(
    props.footer,
    `${CustomerTypes.KNOWN}.${paymentEventType}.0.${MessageConfigTypes.IMAGE}`,
    '',
  );
  const selectedUnknownCustomerImage = get(
    props.footer,
    `${CustomerTypes.UNKNOWN}.0.${MessageConfigTypes.IMAGE}`,
    '',
  );
  const selectedKnownCustomerIcon = get(
    props.footer,
    `${CustomerTypes.KNOWN}.${paymentEventType}.0.${MessageConfigTypes.ICON_AND_TEXT}.icon`,
    '',
  );
  const selectedUnknownCustomerIcon = get(
    props.footer,
    `${CustomerTypes.UNKNOWN}.0.${MessageConfigTypes.ICON_AND_TEXT}.icon`,
    '',
  );

  const handleMessageConfigChange = (
    customerType: CustomerTypes,
    messageType: MessageConfigTypes,
    value: string,
  ) => {
    const fieldPath: Path<CodFooterConfig> =
      customerType === CustomerTypes.KNOWN
        ? `${CustomerTypes.KNOWN}.${paymentEventType}.0.${messageType}`
        : `${CustomerTypes.UNKNOWN}.0.${messageType}`;

    if (messageType === MessageConfigTypes.IMAGE) {
      handleFieldChange(fieldPath)(value);
    } else if (messageType === MessageConfigTypes.ICON_AND_TEXT) {
      const existingLabel = get(
        props.footer,
        `${customerType}.${paymentEventType}.0.${MessageConfigTypes.ICON_AND_TEXT}.label`,
        '',
      );

      handleFieldChange(fieldPath)({
        icon: value,
        label: existingLabel || '',
      });
    }
  };
  const handelClearMessageData = (
    field: Path<CodFooterConfig>,
    customer: CustomerTypes,
  ) => {
    const emptyMessage = {
      ...COD_COMPONENT_CONFIG.codSettings.codSetting.footer.unknownCustomer[0],
      image: '',
      iconAndText: {
        ...COD_COMPONENT_CONFIG.codSettings.codSetting.footer.unknownCustomer[0]
          .iconAndText,
        icon: '',
        label: '',
        ...(rteDefaultStyleValues.fontFamily && {
          font: rteDefaultStyleValues.fontFamily,
        }),
        // Keep the default config's section color (#D6D6D6); do not overwrite
        // it with the base color (#2D2D2D), which hides text on dark screens.
      },
      ...(customer === CustomerTypes.KNOWN && {
        wrapperStyle: {
          ...COD_COMPONENT_CONFIG.codSettings.confirmSetting.footer
            .knownCustomer.linkedPayment[0].wrapperStyle,
          background:
            COD_COMPONENT_CONFIG.codSettings.codSetting.footer.knownCustomer
              .linkedPayment[0].wrapperStyle.background,
          border:
            COD_COMPONENT_CONFIG.codSettings.codSetting.footer.knownCustomer
              .linkedPayment[0].wrapperStyle.border,
        },
      }),
    };
    handleFieldChange(field)(emptyMessage);
  };

  const handleFieldChange = (field: Path<CodFooterConfig>) => (value: any) => {
    props.handleChange(`codSettings.codSetting.footer.${field}`, value);
  };

  const customMessageItems = [
    {
      titleComponent: (
        <MessagingTitle
          label={lang.knownUser}
          hasError={!!props.errors?.knownCustomer}
          isHighlighted={selectedCustomer === CustomerTypes.KNOWN}
          onClick={handleSelectedCustomer(CustomerTypes.KNOWN)}
        />
      ),
      hasError: !!props.errors?.knownCustomer,
      content: (
        <Grid container spacing={2} padding={3}>
          <Grid item xs={12}>
            <FormControl fullWidth>
              <Grid item xs={12}>
                <PaymentTypeToggleButton
                  paymentEventType={paymentEventType}
                  onTogglePaymentEventType={handlePaymentEventType}
                />
              </Grid>

              <RadioGroup
                aria-labelledby="demo-controlled-radio-buttons-group"
                name="controlled-radio-buttons-group"
                sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}
                value={get(
                  props.footer,
                  `${CustomerTypes.KNOWN}.${paymentEventType}.0.enabledTypeKey`,
                )}
                onChange={(e) =>
                  handleFieldChange(
                    `${CustomerTypes.KNOWN}.${paymentEventType}.0.enabledTypeKey`,
                  )(e.target.value)
                }>
                <FormControlLabel
                  value={MessageConfigTypes.IMAGE}
                  control={<Radio />}
                  label={lang.image}
                />

                <Divider />

                <CustomImageSelectionButton
                  showButtonOnly={isEmpty(selectedKnownCustomerImage)}
                  assetName={selectedKnownCustomerImage}
                  handleClick={() => {
                    setLastSelectedAsset(selectedKnownCustomerImage);
                    openResourceDrawer.onOpenModal((value: any) =>
                      handleMessageConfigChange(
                        CustomerTypes.KNOWN,
                        MessageConfigTypes.IMAGE,
                        value,
                      ),
                    );
                  }}
                  handleRemoveImage={() =>
                    handleMessageConfigChange(
                      CustomerTypes.KNOWN,
                      MessageConfigTypes.IMAGE,
                      '',
                    )
                  }
                />

                <FormControlLabel
                  value={MessageConfigTypes.ICON_AND_TEXT}
                  control={<Radio />}
                  label={lang.iconText}
                />

                <Divider />

                <CustomImageSelectionButton
                  showButtonOnly={isEmpty(selectedKnownCustomerIcon)}
                  assetName={selectedKnownCustomerIcon}
                  handleClick={() => {
                    setLastSelectedAsset(selectedKnownCustomerIcon);
                    openResourceDrawer.onOpenModal((value: any) =>
                      handleMessageConfigChange(
                        CustomerTypes.KNOWN,
                        MessageConfigTypes.ICON_AND_TEXT,
                        value,
                      ),
                    );
                  }}
                  handleRemoveImage={() =>
                    handleMessageConfigChange(
                      CustomerTypes.KNOWN,
                      MessageConfigTypes.ICON_AND_TEXT,
                      '',
                    )
                  }
                />
              </RadioGroup>
            </FormControl>
          </Grid>

          <Grid item container spacing={2} xs={12}>
            <Grid item xs={12}>
              <RichTextEditor
                placeholder={lang.enterText}
                value={get(
                  props.footer,
                  `${CustomerTypes.KNOWN}.${paymentEventType}.0.${MessageConfigTypes.ICON_AND_TEXT}.label`,
                )}
                onChange={(value) => {
                  const htmlValue = toString(value);
                  const extractedStyles = extractStylesFromHtml(htmlValue);
                  const currentFont = get(
                    props.footer,
                    `${CustomerTypes.KNOWN}.${paymentEventType}.0.${MessageConfigTypes.ICON_AND_TEXT}.font`,
                  );
                  const labelPath = `codSettings.codSetting.footer.${CustomerTypes.KNOWN}.${paymentEventType}.0.${MessageConfigTypes.ICON_AND_TEXT}.label`;
                  const fontPath = `codSettings.codSetting.footer.${CustomerTypes.KNOWN}.${paymentEventType}.0.${MessageConfigTypes.ICON_AND_TEXT}.font`;
                  const newFont = extractedStyles.fontFamily || currentFont;

                  if (extractedStyles.fontFamily) {
                    props.handleChange(
                      [labelPath, fontPath],
                      [htmlValue, newFont],
                    );
                  } else {
                    props.handleChange(labelPath, htmlValue);
                  }
                }}
                theme={QuillTheme.TOOLBAR}
                fontFamily={mappedFonts}
                fontSize={range(8, 50).map((size) => `${size}px`)}
                letterSpacing={['0px', '1px', '2px']}
                hide={[RichTextToolbarOptions.STRIKE]}
                defaultStyleValues={fontOnlyDefaultStyleValues}
              />
            </Grid>

            <Grid item xs={12}>
              <Button
                variant="text"
                onClick={() =>
                  handelClearMessageData(
                    `${CustomerTypes.KNOWN}.${paymentEventType}.0`,
                    CustomerTypes.KNOWN,
                  )
                }>
                {lang.clear}
              </Button>
            </Grid>
          </Grid>
        </Grid>
      ),
    },
    {
      titleComponent: (
        <MessagingTitle
          label={lang.unknownUser}
          hasError={!!props.errors?.unknownCustomer}
          isHighlighted={selectedCustomer === CustomerTypes.UNKNOWN}
          onClick={handleSelectedCustomer(CustomerTypes.UNKNOWN)}
        />
      ),
      hasError: !!props.errors?.unknownCustomer,
      content: (
        <Grid container spacing={2} padding={3}>
          <Grid item xs={12}>
            <FormControl fullWidth>
              <RadioGroup
                aria-labelledby="demo-controlled-radio-buttons-group"
                name="controlled-radio-buttons-group"
                sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}
                value={get(
                  props.footer,
                  `${CustomerTypes.UNKNOWN}.0.enabledTypeKey`,
                )}
                onChange={(e) => {
                  handleFieldChange(
                    `${CustomerTypes.UNKNOWN}.0.enabledTypeKey`,
                  )(e.target.value);
                }}>
                <FormControlLabel
                  value={MessageConfigTypes.IMAGE}
                  control={<Radio />}
                  label={lang.image}
                />
                <Divider />

                <CustomImageSelectionButton
                  showButtonOnly={isEmpty(selectedUnknownCustomerImage)}
                  assetName={selectedUnknownCustomerImage}
                  handleClick={() => {
                    setLastSelectedAsset(selectedUnknownCustomerImage);
                    openResourceDrawer.onOpenModal((value: any) =>
                      handleMessageConfigChange(
                        CustomerTypes.UNKNOWN,
                        MessageConfigTypes.IMAGE,
                        value,
                      ),
                    );
                  }}
                  handleRemoveImage={() =>
                    handleMessageConfigChange(
                      CustomerTypes.UNKNOWN,
                      MessageConfigTypes.IMAGE,
                      '',
                    )
                  }
                />

                <FormControlLabel
                  value={MessageConfigTypes.ICON_AND_TEXT}
                  control={<Radio />}
                  label={lang.iconText}
                />
                <Divider />
              </RadioGroup>
            </FormControl>

            <CustomImageSelectionButton
              showButtonOnly={isEmpty(selectedUnknownCustomerIcon)}
              assetName={selectedUnknownCustomerIcon}
              handleClick={() => {
                setLastSelectedAsset(selectedUnknownCustomerIcon);
                openResourceDrawer.onOpenModal((value: any) =>
                  handleMessageConfigChange(
                    CustomerTypes.UNKNOWN,
                    MessageConfigTypes.ICON_AND_TEXT,
                    value,
                  ),
                );
              }}
              handleRemoveImage={() =>
                handleMessageConfigChange(
                  CustomerTypes.UNKNOWN,
                  MessageConfigTypes.ICON_AND_TEXT,
                  '',
                )
              }
            />
          </Grid>
          <Grid item container spacing={2} xs={12}>
            <Grid item xs={12}>
              <RichTextEditor
                placeholder={lang.enterText}
                value={get(
                  props.footer,
                  `${CustomerTypes.UNKNOWN}.0.${MessageConfigTypes.ICON_AND_TEXT}.label`,
                )}
                onChange={(value) => {
                  const htmlValue = toString(value);
                  const extractedStyles = extractStylesFromHtml(htmlValue);
                  const currentFont = get(
                    props.footer,
                    `${CustomerTypes.UNKNOWN}.0.${MessageConfigTypes.ICON_AND_TEXT}.font`,
                  );
                  const labelPath = `codSettings.codSetting.footer.${CustomerTypes.UNKNOWN}.0.${MessageConfigTypes.ICON_AND_TEXT}.label`;
                  const fontPath = `codSettings.codSetting.footer.${CustomerTypes.UNKNOWN}.0.${MessageConfigTypes.ICON_AND_TEXT}.font`;
                  const newFont = extractedStyles.fontFamily || currentFont;

                  if (extractedStyles.fontFamily) {
                    props.handleChange(
                      [labelPath, fontPath],
                      [htmlValue, newFont],
                    );
                  } else {
                    props.handleChange(labelPath, htmlValue);
                  }
                }}
                disabled={false}
                theme={QuillTheme.TOOLBAR}
                fontFamily={mappedFonts}
                fontSize={range(8, 50).map((size) => `${size}px`)}
                letterSpacing={['0px', '1px', '2px']}
                hide={[RichTextToolbarOptions.STRIKE]}
                defaultStyleValues={fontOnlyDefaultStyleValues}
              />
            </Grid>

            <Grid item xs={12}>
              <Button
                variant="text"
                onClick={() =>
                  handelClearMessageData(
                    `${CustomerTypes.UNKNOWN}.0`,
                    CustomerTypes.UNKNOWN,
                  )
                }>
                {lang.clear}
              </Button>
            </Grid>
          </Grid>
        </Grid>
      ),
    },
  ];

  return (
    <>
      <SidebarAccordion items={customMessageItems} />

      <ResourceSelectorSidebar
        label=""
        type={ResourceType.BACKGROUND}
        openResourceSelectorDrawer={openResourceDrawer.open}
        onToggleResourceSelectorDrawer={openResourceDrawer.onToggleModal}
        onChange={openResourceDrawer.customFn}
        defaultAssetName={lastSelectedAsset}
      />
    </>
  );
};
